/**
 * Insights Hub Router Configuration
 * 
 * Defines routes for the Insights Hub feature with proper authentication,
 * navigation guards, and parameter handling for Vue.js 2 application.
 */

import { FEATURES } from '@/config/featureConfig';

/**
 * Insights Hub route definitions
 * All routes require INSIGHTS_HUB permission (Professional Plan and above)
 */
export const insightsRoutes = [
  {
    path: '/insights-hub',
    name: 'insights-hub',
    component: () => import('@/views/insights/InsightsHub.vue'),
    meta: {
      title: 'Insights Hub',
      requiredPermission: FEATURES.INSIGHTS_HUB,
      requiresAuth: true,
      breadcrumb: {
        title: 'Insights Hub',
        icon: 'mdi-lightbulb-on-outline'
      },
      description: 'Discover data-driven insights and explore your business intelligence'
    }
  },
  {
    path: '/insights-hub/topic/:topicCode',
    name: 'insights-hub-topic-detail',
    component: () => import('@/views/insights/TopicDetail.vue'),
    props: (route) => ({
      topicCode: route.params.topicCode,
      category: route.query.category,
      filter: route.query.filter,
      sort: route.query.sort || 'relevance'
    }),
    meta: {
      title: 'Topic Detail',
      requiredPermission: FEATURES.INSIGHTS_HUB,
      equiresAuth: true,
      breadcrumb: {
        title: 'Topic Detail',
        parent: 'insights-hub',
        dynamic: true,
        resolver: (route) => ({
          title: `Topic: ${route.params.topicCode}`,
          icon: 'mdi-tag-outline'
        })
      },
      description: 'Explore detailed insights and questions for a specific topic'
    }
  },
  
];

/**
 * Route navigation guards specifically for Insights Hub
 * Handles advanced permission checks and parameter validation
 */
export const insightsNavigationGuards = {
  /**
   * Before entering any insights route, validate parameters and permissions
   */
  beforeEnter: (to, from, next) => {
    // Validate topic code format if present
    if (to.params.topicCode && !/^[a-zA-Z0-9_-]+$/.test(to.params.topicCode)) {
      console.warn('Invalid topic code format:', to.params.topicCode);
      next({ name: 'insights-hub', replace: true });
      return;
    }

    // Validate category name format if present
    if (to.params.categoryName && !/^[a-zA-Z0-9_-]+$/.test(to.params.categoryName)) {
      console.warn('Invalid category name format:', to.params.categoryName);
      next({ name: 'insights-hub', replace: true });
      return;
    }

    // Validate pagination parameters
    const page = parseInt(to.query.page);
    const pageSize = parseInt(to.query.pageSize);
    
    if (to.query.page && (isNaN(page) || page < 1)) {
      const newQuery = { ...to.query, page: '1' };
      next({ ...to, query: newQuery, replace: true });
      return;
    }

    if (to.query.pageSize && (isNaN(pageSize) || pageSize < 1 || pageSize > 100)) {
      const newQuery = { ...to.query, pageSize: '20' };
      next({ ...to, query: newQuery, replace: true });
      return;
    }

    // Validate sort parameters
    const validSortOptions = {
      'insights-hub': ['relevance', 'recent', 'popular'],
      'insights-hub-topic-detail': ['relevance', 'recent', 'alphabetical'],
      'insights-hub-search': ['relevance', 'recent', 'alphabetical'],
      'insights-hub-category': ['popularity', 'recent', 'alphabetical'],
      'insights-hub-recent': ['recent', 'alphabetical', 'category'],
      'insights-hub-pinned': ['order', 'recent', 'alphabetical'],
      'insights-hub-analytics': ['usage', 'performance', 'engagement']
    };

    const validSorts = validSortOptions[to.name];
    if (to.query.sort && validSorts && !validSorts.includes(to.query.sort)) {
      const newQuery = { ...to.query, sort: validSorts[0] };
      next({ ...to, query: newQuery, replace: true });
      return;
    }

    // All validations passed, continue
    next();
  }
};

/**
 * Breadcrumb resolver utilities for dynamic breadcrumb generation
 */
export const insightsBreadcrumbResolvers = {
  /**
   * Resolve breadcrumb for topic detail pages
   * @param {Object} route - Vue route object
   * @param {Object} store - Vuex store (optional)
   * @returns {Object} Breadcrumb configuration
   */
  topicDetail: async (route, store = null) => {
    const topicCode = route.params.topicCode;
    
    // If we have store access, try to get topic name from state
    let topicName = topicCode;
    if (store && store.getters['insights/getTopicByCode']) {
      const topic = store.getters['insights/getTopicByCode'](topicCode);
      if (topic && topic.display_name) {
        topicName = topic.display_name;
      }
    }

    return {
      title: `Topic: ${topicName}`,
      icon: 'mdi-tag-outline',
      params: { topicCode }
    };
  },

  /**
   * Resolve breadcrumb for search results
   * @param {Object} route - Vue route object
   * @returns {Object} Breadcrumb configuration
   */
  searchResults: (route) => {
    const query = route.query.q;
    const title = query ? `Search: "${query}"` : 'Search Results';
    
    return {
      title,
      icon: 'mdi-magnify',
      query: route.query
    };
  },

  /**
   * Resolve breadcrumb for category pages
   * @param {Object} route - Vue route object
   * @returns {Object} Breadcrumb configuration
   */
  category: (route) => {
    const categoryName = route.params.categoryName;
    const displayName = categoryName
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      title: displayName,
      icon: 'mdi-folder-outline',
      params: { categoryName }
    };
  }
};

/**
 * Query parameter helpers for consistent URL management
 */
export const insightsQueryHelpers = {
  /**
   * Build query parameters for topic filtering
   * @param {Object} filters - Filter object
   * @returns {Object} URL query parameters
   */
  buildTopicQuery: (filters = {}) => {
    const query = {};
    
    if (filters.category) query.category = filters.category;
    if (filters.filter) query.filter = filters.filter;
    if (filters.sort) query.sort = filters.sort;
    if (filters.page && filters.page > 1) query.page = filters.page.toString();
    if (filters.pageSize && filters.pageSize !== 20) query.pageSize = filters.pageSize.toString();
    
    return query;
  },

  /**
   * Build query parameters for search
   * @param {Object} searchParams - Search parameters
   * @returns {Object} URL query parameters
   */
  buildSearchQuery: (searchParams = {}) => {
    const query = {};
    
    if (searchParams.q) query.q = searchParams.q;
    if (searchParams.category) query.category = searchParams.category;
    if (searchParams.dateRange) query.dateRange = searchParams.dateRange;
    if (searchParams.sort) query.sort = searchParams.sort;
    if (searchParams.page && searchParams.page > 1) query.page = searchParams.page.toString();
    if (searchParams.pageSize && searchParams.pageSize !== 20) query.pageSize = searchParams.pageSize.toString();
    
    return query;
  },

  /**
   * Parse route query parameters into typed object
   * @param {Object} query - Vue route query object
   * @returns {Object} Parsed parameters
   */
  parseRouteQuery: (query = {}) => {
    return {
      q: query.q || '',
      category: query.category || '',
      filter: query.filter || '',
      sort: query.sort || 'relevance',
      page: parseInt(query.page) || 1,
      pageSize: parseInt(query.pageSize) || 20,
      dateRange: query.dateRange || '',
      timeframe: query.timeframe || '30d',
      metric: query.metric || 'usage',
      breakdown: query.breakdown || '',
      archived: query.archived === 'true',
      subcategory: query.subcategory || ''
    };
  }
};

/**
 * Route metadata helpers for consistent navigation
 */
export const insightsRouteHelpers = {
  /**
   * Get route title with dynamic content
   * @param {Object} route - Vue route object
   * @returns {String} Formatted page title
   */
  getPageTitle: (route) => {
    if (!route.meta || !route.meta.title) return 'Insights Hub';
    
    let title = route.meta.title;
    
    // Add dynamic content based on route
    switch (route.name) {
      case 'insights-hub-topic-detail':
        title = `${title} - ${route.params.topicCode}`;
        break;
      case 'insights-hub-search':
        if (route.query.q) title = `${title} - "${route.query.q}"`;
        break;
      case 'insights-hub-category':
        const categoryDisplay = route.params.categoryName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        title = `${title} - ${categoryDisplay}`;
        break;
    }
    
    return title;
  },

  /**
   * Check if route is within Insights Hub section
   * @param {Object} route - Vue route object
   * @returns {Boolean} True if in insights hub
   */
  isInsightsHubRoute: (route) => {
    return route.name && route.name.startsWith('insights-hub');
  },

  /**
   * Get parent route for navigation
   * @param {Object} route - Vue route object
   * @returns {String|null} Parent route name
   */
  getParentRoute: (route) => {
    if (!route.meta || !route.meta.breadcrumb) return null;
    return route.meta.breadcrumb.parent || null;
  }
};

export default {
  routes: insightsRoutes,
  guards: insightsNavigationGuards,
  breadcrumbs: insightsBreadcrumbResolvers,
  queryHelpers: insightsQueryHelpers,
  routeHelpers: insightsRouteHelpers
};